import { useEffect } from "react";
import { MapContainer, TileLayer, Circle, CircleMarker, Popup, useMap, useMapEvents } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { fmt } from "@/lib/api";

function FitBounds({ points }) {
  const map = useMap();
  useEffect(() => {
    if (points.length > 0) {
      map.fitBounds(points, { padding: [48, 48], maxZoom: 13 });
    }
  }, [JSON.stringify(points)]);
  return null;
}

function ClickHandler({ onMapClick }) {
  useMapEvents({
    click(e) {
      if (onMapClick) onMapClick(e.latlng);
    },
  });
  return null;
}

const STATUS_COLORS = { ACTIVE: "#06b6d4", PAUSED: "#f59e0b", DISABLED: "#64748b", INACTIVE: "#64748b", COMPLETED: "#94a3b8" };

export function GeoMap({
  geofences = [],
  center = [-28.6, 24.7],
  zoom = 6,
  fit = true,
  onSelect,
  onMapClick,
  draftMarker = null,
  draftRadius = 500,
  height = "520px",
  testid = "geo-map",
}) {
  const points = geofences.map((g) => [g.latitude, g.longitude]);
  if (draftMarker) points.push([draftMarker.lat, draftMarker.lng]);

  return (
    <div style={{ height }} className="rounded-md overflow-hidden border border-white/10 relative z-0" data-testid={testid}>
      <MapContainer center={center} zoom={zoom} style={{ height: "100%", width: "100%" }} scrollWheelZoom>
        <TileLayer
          url="https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Dark_Gray_Base/MapServer/tile/{z}/{y}/{x}"
          attribution="Tiles &copy; Esri &mdash; Source: Esri, Maxar, Earthstar Geographics"
        />
        {fit && points.length > 0 && <FitBounds points={points} />}
        <ClickHandler onMapClick={onMapClick} />
        {geofences.map((g) => {
          const color = STATUS_COLORS[g.status] || "#06b6d4";
          const m = g.metrics || {};
          return (
            <span key={g.id}>
              <Circle
                center={[g.latitude, g.longitude]}
                radius={g.radius_meters}
                pathOptions={{ color, fillColor: color, fillOpacity: 0.12, weight: 2 }}
              />
              <CircleMarker
                center={[g.latitude, g.longitude]}
                radius={7}
                pathOptions={{ color: "#ffffff", weight: 2, fillColor: color, fillOpacity: 1 }}
                eventHandlers={{ click: () => onSelect && onSelect(g) }}
              >
                <Popup>
                  <div className="min-w-[220px]">
                    <p className="font-bold text-sm text-white">{g.location_name}</p>
                    {g.campaign_name && <p className="text-xs text-slate-400 mt-0.5">{g.campaign_name}</p>}
                    <p className="text-xs text-slate-400">Radius: {g.radius_meters}m · {g.status}</p>
                    {m.impressions !== undefined && (
                      <div className="grid grid-cols-2 gap-x-3 gap-y-1 mt-2 text-xs">
                        <span className="text-slate-400">Impressions</span><span className="text-right font-semibold text-cyan-300">{fmt(m.impressions)}</span>
                        <span className="text-slate-400">Reach</span><span className="text-right font-semibold text-cyan-300">{fmt(m.reach)}</span>
                        <span className="text-slate-400">Engagements</span><span className="text-right font-semibold text-cyan-300">{fmt(m.engagements)}</span>
                        <span className="text-slate-400">Eng. Rate</span><span className="text-right font-semibold text-cyan-300">{m.engagement_rate}%</span>
                      </div>
                    )}
                    {onSelect && (
                      <button
                        onClick={() => onSelect(g)}
                        className="mt-2 w-full text-xs font-semibold text-white bg-blue-600 hover:bg-blue-500 rounded px-2 py-1.5 transition-colors"
                        data-testid={`map-open-${g.id}`}
                      >
                        Open Location Intelligence
                      </button>
                    )}
                  </div>
                </Popup>
              </CircleMarker>
            </span>
          );
        })}
        {draftMarker && (
          <>
            <Circle
              center={[draftMarker.lat, draftMarker.lng]}
              radius={draftRadius}
              pathOptions={{ color: "#3b82f6", fillColor: "#3b82f6", fillOpacity: 0.15, weight: 2, dashArray: "6 6" }}
            />
            <CircleMarker
              center={[draftMarker.lat, draftMarker.lng]}
              radius={8}
              pathOptions={{ color: "#ffffff", weight: 2, fillColor: "#3b82f6", fillOpacity: 1 }}
            />
          </>
        )}
      </MapContainer>
    </div>
  );
}

export default GeoMap;
